import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { ShieldAlert, CheckCircle2, ChevronDown } from 'lucide-react';
import { getRideAlerts, resolveRideAlert } from '../api/rideAlerts';
import { useToast } from '../context/ToastContext';
import { Table } from '../components/ui/Table';
import Badge from '../components/ui/Badge';
import Button from '../components/ui/Button';
import Pagination from '../components/ui/Pagination';
import { formatDate, getErrorMessage } from '../lib/utils';

const PAGE_SIZE = 20;

const STATUS_FILTERS = [
  { value: '', label: 'All' },
  { value: 'open', label: 'Open' },
  { value: 'acknowledged', label: 'Acknowledged' },
  { value: 'resolved', label: 'Resolved' },
];

const TYPE_LABELS = {
  sos: 'SOS',
  route_deviation: 'Route Deviation',
};

function statusVariant(status) {
  if (status === 'open') return 'danger';
  if (status === 'acknowledged') return 'warning';
  if (status === 'resolved') return 'success';
  return 'default';
}

export default function SafetyAlerts() {
  const { addToast } = useToast();
  const qc = useQueryClient();
  const [statusFilter, setStatusFilter] = useState('');
  const [page, setPage] = useState(1);

  function handleStatusFilterChange(value) {
    setStatusFilter(value);
    setPage(1);
  }

  const { data, isLoading } = useQuery({
    queryKey: ['ride-alerts', statusFilter, page],
    queryFn: () =>
      getRideAlerts({ ...(statusFilter ? { status: statusFilter } : {}), page, limit: PAGE_SIZE }).then((r) => r.data),
    refetchInterval: 15000,
  });

  const alerts = data?.items ?? [];
  const total = data?.total ?? 0;

  const resolve = useMutation({
    mutationFn: ({ id, status }) => resolveRideAlert(id, { status }),
    onSuccess: (_, { status }) => {
      qc.invalidateQueries({ queryKey: ['ride-alerts'] });
      addToast(status === 'resolved' ? 'Alert resolved' : 'Alert acknowledged', 'success');
    },
    onError: (err) => addToast(getErrorMessage(err, 'Failed to update alert'), 'error'),
  });

  const columns = [
    { key: 'id', label: 'ID', width: 60, render: (v) => <span className="font-mono text-xs text-gray-500">#{v}</span> },
    {
      key: 'type', label: 'Type',
      render: (v) => (
        <span className="inline-flex items-center gap-1.5 font-medium text-gray-900 dark:text-gray-100">
          <ShieldAlert size={14} className={v === 'sos' ? 'text-red-500' : 'text-amber-500'} />
          {TYPE_LABELS[v] ?? v}
        </span>
      ),
    },
    { key: 'ride_id', label: 'Ride', render: (v) => (v != null ? <span className="font-mono text-xs">#{v}</span> : '—') },
    {
      key: 'raised_by', label: 'Raised by',
      render: (_, row) => row.driver_id != null
        ? <span>{row.driver_name ?? `Driver #${row.driver_id}`}</span>
        : <span>{row.customer_name ?? `Customer #${row.customer_id}`}</span>,
    },
    {
      key: 'lat', label: 'Location',
      render: (v, row) => row.address ?? (v != null && row.lng != null ? `${v.toFixed(4)}, ${row.lng.toFixed(4)}` : '—'),
    },
    { key: 'status', label: 'Status', render: (v) => <Badge variant={statusVariant(v)}>{v}</Badge> },
    { key: 'created_at', label: 'Raised', render: (v) => <span className="text-xs text-gray-500 whitespace-nowrap">{formatDate(v)}</span> },
    {
      key: 'actions', label: '',
      render: (_, row) => (
        <div className="flex items-center gap-1" onClick={(e) => e.stopPropagation()}>
          {row.status === 'open' && (
            <Button size="xs" variant="ghost" onClick={() => resolve.mutate({ id: row.id, status: 'acknowledged' })}>
              Acknowledge
            </Button>
          )}
          {row.status !== 'resolved' && (
            <Button
              size="xs"
              variant="ghost"
              icon={CheckCircle2}
              className="text-green-600 hover:bg-green-50 dark:text-green-400 dark:hover:bg-green-900/20"
              onClick={() => resolve.mutate({ id: row.id, status: 'resolved' })}
            >
              Resolve
            </Button>
          )}
        </div>
      ),
    },
  ];

  return (
    <div className="space-y-5">
      <div className="flex items-center justify-between gap-3">
        <div>
          <h2 className="text-xl font-bold text-gray-900 dark:text-gray-100">Safety Alerts</h2>
          <p className="text-sm text-gray-500 dark:text-gray-400">{total} alerts</p>
        </div>
        <div className="relative">
          <select
            value={statusFilter}
            onChange={(e) => handleStatusFilterChange(e.target.value)}
            className="appearance-none rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 pl-3 pr-8 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-amber-500"
          >
            {STATUS_FILTERS.map((f) => (
              <option key={f.value} value={f.value}>{f.label}</option>
            ))}
          </select>
          <ChevronDown size={16} className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-gray-400" />
        </div>
      </div>

      <Table columns={columns} data={alerts} loading={isLoading} emptyMessage="No safety alerts" />
      {!isLoading && alerts.length > 0 && (
        <Pagination page={page} limit={PAGE_SIZE} total={total} onPageChange={setPage} />
      )}
    </div>
  );
}
